import React, { useState, useEffect } from 'react';
import { Stack, Button, TextField, Table, TableBody, TableRow, TableCell, IconButton } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import StepSection from './StepSection';
import MethodSelect from './MethodSelect';
import FieldAutocomplete from './FieldAutocomplete';
import DirectionSelect from './DirectionSelect';
import ChipList from './ChipList';
import { SORT_METHODS } from './constants';
import { Alert } from '@mui/material';
import { buildSortConfig } from './rankingConfigBuilders';

const defaultSortState = {
  enabled: false,
  method: 'sort',
  sortField1: '',
  sortDirection1: 'desc',
  sortField2: '',
  sortDirection2: 'desc',
  linearField: '',
  linearWeight: 1,
  linearPairs: [],
  mixField: '',
  mixDirection: 'desc',
  mixRows: [],
};

export default function SortingStep({ onConfigChange, availableFields }) {
  const [state, setState] = useState(defaultSortState);

  useEffect(() => {
    const config = buildSortConfig(state);
    onConfigChange?.({ enabled: state.enabled, config });
  }, [state, onConfigChange]);

  const update = (partial) => setState((prev) => ({ ...prev, ...partial }));

  const addLinearPair = () => {
    const field = (state.linearField && state.linearField.trim()) || state.linearField;
    if (!field || state.linearPairs.some((p) => p.field === field)) return;
    setState((prev) => ({
      ...prev,
      linearPairs: [...prev.linearPairs, { field, weight: Number(prev.linearWeight) || 0 }],
      linearField: '',
      linearWeight: 1,
    }));
  };

  const addMixRow = () => {
    const field = (state.mixField && state.mixField.trim()) || state.mixField;
    if (!field) return;
    setState((prev) => ({
      ...prev,
      mixRows: [...prev.mixRows, { field, direction: prev.mixDirection, percentage: 0 }],
      mixField: '',
    }));
  };

  const updateMixRowPercentage = (index, value) => {
    const pct = Number(value);
    setState((prev) => ({
      ...prev,
      mixRows: prev.mixRows.map((row, i) =>
        i === index ? { ...row, percentage: Number.isFinite(pct) ? pct : 0 } : row
      ),
    }));
  };

  const linearChipItems = state.linearPairs.map((p, i) => ({
    label: `${p.field}:${p.weight}`,
    key: `${p.field}-${i}`,
  }));

  return (
    <StepSection
      title="1) Sorting"
      enabled={state.enabled}
      onEnabledChange={(enabled) => update({ enabled })}
    >
      <>
        <MethodSelect
          options={SORT_METHODS}
          value={state.method}
          onChange={(method) => update({ method })}
        />

        {state.method === 'sort' && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <FieldAutocomplete
                options={availableFields}
                value={state.sortField1}
                onChange={(sortField1) => update({ sortField1 })}
                label="Sort column"
                sx={{ flex: 1 }}
              />
              <DirectionSelect
                value={state.sortDirection1}
                onChange={(sortDirection1) => update({ sortDirection1 })}
              />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <FieldAutocomplete
                options={availableFields}
                value={state.sortField2}
                onChange={(sortField2) => update({ sortField2 })}
                label="Second (optional)"
                sx={{ flex: 1 }}
              />
              <DirectionSelect
                value={state.sortDirection2}
                onChange={(sortDirection2) => update({ sortDirection2 })}
              />
            </Stack>
          </Stack>
        )}

        {state.method === 'linear' && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <FieldAutocomplete
                options={availableFields}
                value={state.linearField}
                onChange={(linearField) => update({ linearField })}
                label="Field"
                sx={{ minWidth: 140, flex: 1 }}
              />
              <TextField
                size="small"
                type="number"
                label="Weight"
                value={state.linearWeight}
                onChange={(e) => update({ linearWeight: e.target.value })}
                sx={{ width: 100 }}
                inputProps={{ step: 0.1 }}
              />
              <Button size="small" variant="outlined" onClick={addLinearPair}>
                Add
              </Button>
            </Stack>
            <ChipList
              items={linearChipItems}
              onDelete={(idx) =>
                setState((prev) => ({
                  ...prev,
                  linearPairs: prev.linearPairs.filter((_, i) => i !== idx),
                }))
              }
            />
          </Stack>
        )}

        {state.method === 'mix' && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <FieldAutocomplete
                options={availableFields}
                value={state.mixField}
                onChange={(mixField) => update({ mixField })}
                label="Field"
                sx={{ minWidth: 120, flex: 1 }}
              />
              <DirectionSelect
                value={state.mixDirection}
                onChange={(mixDirection) => update({ mixDirection })}
              />
              <Button size="small" variant="outlined" onClick={addMixRow}>
                Add
              </Button>
            </Stack>
            {state.mixRows.length > 0 && (
              <>
                <Table size="small" padding="none">
                  <TableBody>
                    {state.mixRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.field}</TableCell>
                        <TableCell>{row.direction}</TableCell>
                        <TableCell padding="none" sx={{ width: 90 }}>
                          <TextField
                            size="small"
                            type="number"
                            placeholder="%"
                            value={row.percentage === 0 ? '' : row.percentage}
                            onChange={(e) => updateMixRowPercentage(idx, e.target.value)}
                            inputProps={{ min: 0, max: 100, step: 1 }}
                            sx={{ width: 70 }}
                          />
                        </TableCell>
                        <TableCell padding="none">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setState((prev) => ({
                                ...prev,
                                mixRows: prev.mixRows.filter((_, i) => i !== idx),
                              }))
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {state.mixRows.reduce((sum, r) => sum + (Number(r.percentage) || 0), 0) !== 100 && (
                  <Alert severity="info" sx={{ py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                    Percentages should sum to 100.
                  </Alert>
                )}
              </>
            )}
          </Stack>
        )}
      </>
    </StepSection>
  );
}
